import React from "react";
import {
  Alert,
  Box,
  Button,
  CardContent,
  Chip,
  Grid,
  LinearProgress,
  Stack,
  Typography,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import ShoppingCartCheckoutRoundedIcon from "@mui/icons-material/ShoppingCartCheckoutRounded";
import TrendingUpRoundedIcon from "@mui/icons-material/TrendingUpRounded";
import { useNavigate, useOutletContext, useParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { ErpEmpty, ErpLoading, ErpMetricCard, ErpPanel, ErpSectionTitle, MovementStatusChip } from "../../../components/dashboard/erp/ErpUi";
import { ERP_BRAND, MOVEMENT_KIND_LABELS, formatDateTime, formatMoney } from "../../../utils/erp";

export default function ProjectErpOverviewPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { bootstrap } = useOutletContext();
  const [overview, setOverview] = React.useState(null);
  const [registers, setRegisters] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const [overviewData, cashData] = await Promise.all([
        erpApi.overview(projectId),
        erpApi.listCashRegisters(projectId),
      ]);
      setOverview(overviewData.overview || null);
      setRegisters(cashData.registers || []);
    } catch (err) {
      setError(err.message || "No se pudo cargar el resumen del ERP");
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  React.useEffect(() => {
    load();
  }, [load]);

  if (loading) return <ErpLoading label="Preparando el resumen operativo..." />;

  const sales = overview?.sales || {};
  const purchases = overview?.purchases || {};
  const ledger = overview?.ledger || {};
  const recent = overview?.recentMovements || [];
  const balanced = Number(ledger.debits || 0) === Number(ledger.credits || 0);

  return (
    <Stack spacing={2.5}>
      {error ? <Alert severity="error" action={<Button color="inherit" onClick={load}>Reintentar</Button>}>{error}</Alert> : null}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} xl={3}>
          <ErpMetricCard
            icon={<TrendingUpRoundedIcon />}
            label="VENTAS CONFIRMADAS"
            value={formatMoney(sales.total)}
            detail={`${sales.count || 0} movimientos · ${formatMoney(sales.pending)} pendiente`}
            tone="green"
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <ErpMetricCard
            icon={<ShoppingCartCheckoutRoundedIcon />}
            label="COMPRAS CONFIRMADAS"
            value={formatMoney(purchases.total)}
            detail={`${purchases.count || 0} movimientos · ${formatMoney(purchases.pending)} pendiente`}
            tone="orange"
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <ErpMetricCard
            icon={<PointOfSaleRoundedIcon />}
            label="CAJAS ABIERTAS"
            value={String(overview?.openCashSessions || 0)}
            detail={`${registers.length || bootstrap.registers.length || 0} cajas configuradas`}
            tone="blue"
          />
        </Grid>
        <Grid item xs={12} sm={6} xl={3}>
          <ErpMetricCard
            icon={<AccountBalanceRoundedIcon />}
            label="LIBRO DIARIO"
            value={`${ledger.entries || 0} líneas`}
            detail={balanced ? "Debe y haber balanceados" : "Revisar diferencia contable"}
            tone={balanced ? "dark" : "red"}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2.5}>
        <Grid item xs={12} lg={7.5}>
          <ErpPanel>
            <CardContent sx={{ p: 3 }}>
              <ErpSectionTitle
                title="Actividad reciente"
                description="Últimos movimientos confirmados o anulados del proyecto."
                action={
                  <Button
                    size="small"
                    onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos`)}
                    sx={{ textTransform: "none", fontWeight: 850 }}
                  >
                    Ver todos
                  </Button>
                }
              />

              {!recent.length ? (
                <ErpEmpty
                  title="Todavía no hay movimientos"
                  description="Registrá la primera venta, compra o entrada de stock para comenzar a construir el historial del negocio."
                  action={
                    <Button
                      variant="contained"
                      startIcon={<AddRoundedIcon />}
                      onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos?nuevo=1`)}
                      sx={{ borderRadius: 2.5, textTransform: "none", fontWeight: 850 }}
                    >
                      Crear movimiento
                    </Button>
                  }
                />
              ) : (
                <Stack spacing={0} sx={{ mt: 2 }}>
                  {recent.map((movement, index) => (
                    <Stack
                      key={movement.id || movement._id}
                      direction={{ xs: "column", sm: "row" }}
                      justifyContent="space-between"
                      spacing={1.5}
                      sx={{
                        py: 1.8,
                        borderTop: index ? `1px solid ${ERP_BRAND.border}` : "none",
                      }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                          <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>
                            {movement.movementNumber}
                          </Typography>
                          <Chip size="small" label={MOVEMENT_KIND_LABELS[movement.kind] || movement.kind} />
                          <MovementStatusChip status={movement.status} />
                        </Stack>
                        <Typography noWrap sx={{ color: ERP_BRAND.muted, mt: 0.65 }}>
                          {movement.description || movement.party?.name || "Sin descripción"}
                        </Typography>
                      </Box>
                      <Box sx={{ textAlign: { xs: "left", sm: "right" }, flexShrink: 0 }}>
                        <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>
                          {formatMoney(movement.total, movement.currency)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                          {formatDateTime(movement.occurredAt)}
                        </Typography>
                      </Box>
                    </Stack>
                  ))}
                </Stack>
              )}
            </CardContent>
          </ErpPanel>
        </Grid>

        <Grid item xs={12} lg={4.5}>
          <Stack spacing={2.5}>
            <ErpPanel>
              <CardContent sx={{ p: 3 }}>
                <ErpSectionTitle title="Acciones rápidas" description="Atajos para la operación diaria." />
                <Stack spacing={1.2} sx={{ mt: 2 }}>
                  <Button
                    fullWidth
                    variant="contained"
                    startIcon={<ReceiptLongRoundedIcon />}
                    onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos?nuevo=1`)}
                    sx={{ justifyContent: "flex-start", borderRadius: 2.5, py: 1.2, textTransform: "none", fontWeight: 850 }}
                  >
                    Nueva venta, compra o movimiento
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<PointOfSaleRoundedIcon />}
                    onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/caja`)}
                    sx={{ justifyContent: "flex-start", borderRadius: 2.5, py: 1.2, textTransform: "none", fontWeight: 850 }}
                  >
                    Administrar cajas y arqueos
                  </Button>
                  <Button
                    fullWidth
                    variant="outlined"
                    startIcon={<AccountBalanceRoundedIcon />}
                    onClick={() => navigate(`/dashboard/proyectos/${projectId}/sistema/ledger`)}
                    sx={{ justifyContent: "flex-start", borderRadius: 2.5, py: 1.2, textTransform: "none", fontWeight: 850 }}
                  >
                    Revisar libro diario
                  </Button>
                </Stack>
              </CardContent>
            </ErpPanel>

            <ErpPanel>
              <CardContent sx={{ p: 3 }}>
                <ErpSectionTitle title="Estado contable" description="Control automático de doble partida." />
                <Stack spacing={1.6} sx={{ mt: 2 }}>
                  <Box>
                    <Stack direction="row" justifyContent="space-between" spacing={1}>
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>Debe</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>{formatMoney(ledger.debits)}</Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between" spacing={1} sx={{ mt: 0.8 }}>
                      <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>Haber</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 900 }}>{formatMoney(ledger.credits)}</Typography>
                    </Stack>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={balanced ? 100 : 55}
                    color={balanced ? "success" : "warning"}
                    sx={{ height: 8, borderRadius: 999 }}
                  />
                  <Alert severity={balanced ? "success" : "warning"} sx={{ borderRadius: 2.5 }}>
                    {balanced ? "El libro está balanceado." : "Existe una diferencia entre debe y haber."}
                  </Alert>
                </Stack>
              </CardContent>
            </ErpPanel>
          </Stack>
        </Grid>
      </Grid>
    </Stack>
  );
}
